import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { SnackbarContent, closeSnackbar, enqueueSnackbar } from 'notistack';
import type {
  CustomContentProps,
  SnackbarKey,
  SnackbarMessage,
} from 'notistack';
import { forwardRef } from 'react';
import {
  ARENA_TONES,
  ArenaAction,
  ArenaBody,
  ArenaClose,
  ArenaGlyph,
  ArenaLabel,
  ArenaMessage,
  ArenaPlate,
  ArenaTimer,
  type ArenaTone,
} from './index.styles';

const GLYPHS: Record<ArenaTone, typeof InfoRoundedIcon> = {
  success: CheckRoundedIcon,
  error: ErrorOutlineRoundedIcon,
  info: InfoRoundedIcon,
  warning: WarningAmberRoundedIcon,
  nudge: BoltRoundedIcon,
};

const DEFAULT_DURATION = 5000;

// A type alias, not an interface: notistack's `VariantOverrides` filter keeps
// only members assignable to `Record<string, unknown>`, and an interface has no
// implicit index signature — declared as one, the variant is silently dropped.
export type ArenaSnackbarOptions = {
  tone?: ArenaTone;
  /** Overrides the tone's default eyebrow ("Nice", "Nudge", …). */
  label?: string;
  actionLabel?: string;
  onAction?: () => void;
  duration?: number;
};

declare module 'notistack' {
  interface VariantOverrides {
    arena: ArenaSnackbarOptions;
  }
}

/**
 * The arena's own toast.
 *
 * notistack's stock variants paint a solid red or blue block, which reads as a
 * browser-level system error rather than as something the match said — and
 * looks like a different product sitting on top of a dark arena. This keeps the
 * arena surface and carries the tone on an edge accent and a glyph instead.
 */
const ArenaSnackbar = forwardRef<
  HTMLDivElement,
  CustomContentProps & ArenaSnackbarOptions
>(
  (
    {
      id,
      message,
      tone = 'info',
      label,
      actionLabel,
      onAction,
      duration = DEFAULT_DURATION,
      style,
    },
    ref,
  ) => {
    const Glyph = GLYPHS[tone];
    return (
      <SnackbarContent ref={ref} role="alert" style={style}>
        <ArenaPlate tone={tone}>
          <ArenaGlyph tone={tone}>
            <Glyph sx={{ fontSize: 19 }} />
          </ArenaGlyph>
          <ArenaBody>
            <ArenaLabel tone={tone}>
              {label ?? ARENA_TONES[tone].label}
            </ArenaLabel>
            <ArenaMessage>{message}</ArenaMessage>
          </ArenaBody>
          {!!actionLabel && (
            <ArenaAction
              role="button"
              tabIndex={0}
              onClick={() => {
                onAction?.();
                closeSnackbar(id);
              }}
            >
              {actionLabel}
            </ArenaAction>
          )}
          <ArenaClose
            role="button"
            tabIndex={0}
            aria-label="Dismiss"
            onClick={() => closeSnackbar(id)}
          >
            <CloseRoundedIcon sx={{ fontSize: 17 }} />
          </ArenaClose>
          <ArenaTimer tone={tone} duration={duration} />
        </ArenaPlate>
      </SnackbarContent>
    );
  },
);

ArenaSnackbar.displayName = 'ArenaSnackbar';

/**
 * Every message multiplayer shows goes through here rather than through
 * `enqueueSnackbar` directly, so the arena never mixes its own toast with the
 * platform's default one mid-match.
 */
export const arenaToast = (
  message: SnackbarMessage,
  options: ArenaSnackbarOptions = {},
): SnackbarKey =>
  enqueueSnackbar(message, {
    variant: 'arena',
    autoHideDuration: options.duration ?? DEFAULT_DURATION,
    ...options,
  });

export default ArenaSnackbar;
